import { useState, type CSSProperties, type ReactNode } from 'react';
import {
  localTimeParts,
  type DayWindow,
  type DayWindows,
  type Panchanga,
  type WindowQuality,
} from '@astrally/astro-engine';
// GRAHA_NAMES deliberately from @astrally/shared — the engine exports a
// different table under the same name; never import both in one file.
import { GRAHA_NAMES, type GrahaBody, type Planet } from '@astrally/shared';
import { InfoDot } from '../../components/common';
import { Term } from '../../components/Term';

/**
 * One traditional day, drawn honestly.
 *
 * The day here is the *Hindu* day — sunrise to the next sunrise — because
 * every window below is a fraction of the real daylight and night length at
 * one place. That is why the same clock time is a different window in Mumbai
 * and in London, and why this component always needs a longitude.
 *
 * Two representations, deliberately:
 *   1. A proportional bar for the shape of the day at a glance. It carries no
 *      text, so it is never the only place a fact appears.
 *   2. Labelled rows underneath carrying every window by name, in words, with
 *      its clock times and the engine's own plain-English note.
 *
 * COLOUR IS NEVER THE ONLY SIGNAL. Every block is also distinguished by
 * pattern (diagonal hatch = avoid, vertical pinstripe = favourable, flat =
 * ordinary) and, more importantly, by the word "Avoid" or "Favourable" printed
 * beside its name. Someone who cannot see the bar at all loses nothing.
 *
 * Everything rendered here comes from the engine's `computeDayWindows()`. This
 * file computes no astrology of its own — it only lays out and explains what
 * the engine produced.
 */

const MS_PER_MIN = 60_000;

/**
 * The engine names a day's ruler by planet id ('saturn'); the reader gets the
 * graha's own name (Shani). Weekday lords are always the seven classical
 * grahas, so the fallback only guards an engine change.
 */
function grahaName(planet: Planet): string {
  return planet in GRAHA_NAMES ? GRAHA_NAMES[planet as GrahaBody] : planet;
}

/* --- fills -----------------------------------------------------------------

   Only tokens from global.css. The pattern angle and pitch differ between the
   two graded fills so they stay distinguishable in greyscale, in both themes,
   and for anyone who does not separate red from green. */

const FILL_AVOID: CSSProperties = {
  background: 'repeating-linear-gradient(135deg, var(--danger) 0 2px, var(--danger-soft) 2px 7px)',
  border: '1px solid var(--danger-line)',
};

const FILL_GOOD: CSSProperties = {
  background: 'repeating-linear-gradient(90deg, var(--ok) 0 2px, var(--ok-soft) 2px 7px)',
  border: '1px solid var(--ok)',
};

const FILL_PLAIN: CSSProperties = {
  background: 'var(--card-strong)',
  border: '1px solid var(--border)',
};

function fillFor(quality: WindowQuality): CSSProperties {
  if (quality === 'inauspicious') return FILL_AVOID;
  if (quality === 'auspicious') return FILL_GOOD;
  return FILL_PLAIN;
}

const QUALITY_WORD: Record<WindowQuality, string> = {
  auspicious: 'Favourable',
  inauspicious: 'Avoid',
  neutral: 'Ordinary',
};

const QUALITY_INK: Record<WindowQuality, string> = {
  auspicious: 'var(--ok)',
  inauspicious: 'var(--danger)',
  neutral: 'var(--text-dim)',
};

/* --- local clock helpers ---------------------------------------------------

   The engine's localTimeParts is the single source of local wall-clock time:
   it falls back to mean solar time at the longitude when no timezone is known,
   which is what the classical calendar actually uses. Formatting to 12-hour is
   presentation only — India reads "5:48 am", not "05:48". */

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export function clockAt(utcMs: number, lon: number, timezone?: string): string {
  const { time } = localTimeParts(utcMs, lon, timezone);
  const [h, m] = time.split(':').map(Number);
  const suffix = h < 12 ? 'am' : 'pm';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${suffix}`;
}

export function dateAt(utcMs: number, lon: number, timezone?: string): string {
  const { date } = localTimeParts(utcMs, lon, timezone);
  const [y, mo, d] = date.split('-').map(Number);
  return `${d} ${MONTHS[mo - 1]} ${y}`;
}

function minutesBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / MS_PER_MIN);
}

/** "1 h 34 m" / "48 min" — a duration someone can act on without arithmetic. */
function durationLabel(mins: number): string {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} m`;
}

/**
 * The choghadiya notes the engine writes read "Amrit choghadiya, 'nectar' —
 * …". The name is already the row heading, so the prefix is dropped for
 * display. If the engine's wording ever changes the replace simply no-ops and
 * the full note is shown, which is still correct.
 */
function choghadiyaGloss(note: string): string {
  return note.replace(/^[A-Za-z]+ choghadiya, /, '');
}

/* --- pieces ---------------------------------------------------------------- */

function Swatch({ quality, stretch }: { quality: WindowQuality; stretch?: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        width: 10,
        minWidth: 10,
        borderRadius: 4,
        alignSelf: stretch ? 'stretch' : undefined,
        minHeight: 26,
        ...fillFor(quality),
      }}
    />
  );
}

function LegendKey({ quality, children }: { quality: WindowQuality; children: ReactNode }) {
  return (
    <span className="row tiny muted" style={{ gap: 6 }}>
      <span
        aria-hidden
        style={{ width: 16, height: 10, minWidth: 16, borderRadius: 3, ...fillFor(quality) }}
      />
      {children}
    </span>
  );
}

/** One named window: swatch, name, verdict word, clock times, engine note. */
function NamedRow({
  window: w,
  lon,
  timezone,
  term,
}: {
  window: DayWindow;
  lon: number;
  timezone?: string;
  /** Glossary id for an InfoDot beside the name, when one exists. */
  term?: string;
}) {
  const mins = minutesBetween(w.startUtc, w.endUtc);
  return (
    <div
      className="row"
      style={{ alignItems: 'stretch', gap: 10, padding: '8px 0', borderTop: '1px solid var(--border)' }}
    >
      <Swatch quality={w.quality} stretch />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="spread" style={{ alignItems: 'baseline', gap: 8 }}>
          <span className="h3" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {w.label}
            {term && <InfoDot term={term} />}
          </span>
          <span className="tiny muted" style={{ whiteSpace: 'nowrap' }}>
            {clockAt(w.startUtc.getTime(), lon, timezone)} – {clockAt(w.endUtc.getTime(), lon, timezone)}
          </span>
        </div>
        <div className="tiny" style={{ color: QUALITY_INK[w.quality], fontWeight: 600, marginTop: 2 }}>
          {QUALITY_WORD[w.quality]} · {durationLabel(mins)}
        </div>
        <div className="tiny muted" style={{ marginTop: 4, lineHeight: 1.55 }}>
          {w.note}
        </div>
      </div>
    </div>
  );
}

/**
 * One choghadiya band. The eighths of daylight that rahu kaal, yamaganda and
 * gulika occupy are the SAME eighths the day choghadiya divide into, so a
 * favourable band can sit exactly underneath an inauspicious one. The
 * tradition is unambiguous about which wins, and so is this row: the blocked
 * band is graded "Avoid" and says why.
 */
function BandRow({
  band,
  blocker,
  lon,
  timezone,
}: {
  band: DayWindow;
  blocker: DayWindow | null;
  lon: number;
  timezone?: string;
}) {
  const effective: WindowQuality = blocker ? 'inauspicious' : band.quality;
  return (
    <div
      className="row"
      style={{ alignItems: 'stretch', gap: 10, padding: '7px 0', borderTop: '1px solid var(--border)' }}
    >
      <Swatch quality={effective} stretch />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="spread" style={{ alignItems: 'baseline', gap: 8 }}>
          <span className="small" style={{ fontWeight: 600 }}>
            {band.choghadiya}
            <span className="tiny" style={{ color: QUALITY_INK[effective], fontWeight: 600, marginLeft: 8 }}>
              {QUALITY_WORD[effective]}
            </span>
          </span>
          <span className="tiny muted" style={{ whiteSpace: 'nowrap' }}>
            {clockAt(band.startUtc.getTime(), lon, timezone)} – {clockAt(band.endUtc.getTime(), lon, timezone)}
          </span>
        </div>
        <div className="tiny faint" style={{ marginTop: 3, lineHeight: 1.5 }}>
          {choghadiyaGloss(band.note)}
        </div>
        {blocker && (
          <div className="tiny" style={{ color: 'var(--danger)', marginTop: 3, lineHeight: 1.5 }}>
            {blocker.label} covers this band, and an inauspicious period outranks a favourable one.
          </div>
        )}
      </div>
    </div>
  );
}

/* --- the component --------------------------------------------------------- */

export interface WindowTimelineProps {
  /** Everything drawn here — computed by the engine, never in this file. */
  windows: DayWindows;
  /** Longitude of the place the day was cut for. Required for local clock times. */
  lon: number;
  /** IANA timezone. Omitted falls back to mean solar time at `lon`. */
  timezone?: string;
  /** Shown in the header so a reader knows whose sunrise this is. */
  placeLabel?: string;
  /** Draws a "now" needle when the instant falls inside this day. */
  nowUtc?: Date;
  /** Outlines a chosen window on the bar, e.g. the suggestion this day came from. */
  highlight?: { startUtc: Date; endUtc: Date; label: string };
  /** The five limbs at sunrise, when the caller has chosen to compute them. */
  panchanga?: Panchanga;
  /** Start with the 16 choghadiya bands expanded. Default true. */
  defaultShowBands?: boolean;
}

export function WindowTimeline({
  windows,
  lon,
  timezone,
  placeLabel,
  nowUtc,
  highlight,
  panchanga,
  defaultShowBands = true,
}: WindowTimelineProps) {
  const [showBands, setShowBands] = useState(defaultShowBands);

  const { day } = windows;
  // The bar spans everything the engine placed in this day. Brahma muhurta
  // opens it — it falls before its own sunrise, which is correct: the 96
  // minutes before dawn belong to the day that is about to begin.
  const spanStart = Math.min(...windows.windows.map((w) => w.startUtc.getTime()));
  const spanEnd = Math.max(...windows.windows.map((w) => w.endUtc.getTime()));
  const spanMs = Math.max(spanEnd - spanStart, 1);

  const pct = (ms: number) => ((ms - spanStart) / spanMs) * 100;
  const blockAt = (w: { startUtc: Date; endUtc: Date }): CSSProperties => ({
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: `${Math.max(0, pct(w.startUtc.getTime()))}%`,
    width: `${Math.max(0.6, pct(w.endUtc.getTime()) - pct(w.startUtc.getTime()))}%`,
  });

  const named = windows.windows.filter((w) => !w.kind.startsWith('choghadiya'));
  const nowMs = nowUtc?.getTime();
  const nowInside = nowMs !== undefined && nowMs >= spanStart && nowMs <= spanEnd;

  /** Which of the three inauspicious eighths, if any, covers a band. */
  const blockerFor = (band: DayWindow): DayWindow | null => {
    const mid = (band.startUtc.getTime() + band.endUtc.getTime()) / 2;
    return (
      [windows.rahuKaal, windows.yamaganda, windows.gulika].find(
        (b) => b.startUtc.getTime() <= mid && mid < b.endUtc.getTime(),
      ) ?? null
    );
  };

  // The bar is decorative; this sentence is what a screen reader gets instead.
  const barSummary = [
    `Day at ${placeLabel ?? 'this place'}, ${dateAt(day.sunriseUtc.getTime(), lon, timezone)}.`,
    `Sunrise ${clockAt(day.sunriseUtc.getTime(), lon, timezone)}, sunset ${clockAt(day.sunsetUtc.getTime(), lon, timezone)}.`,
    ...named.map(
      (w) =>
        `${w.label}, ${clockAt(w.startUtc.getTime(), lon, timezone)} to ${clockAt(w.endUtc.getTime(), lon, timezone)}, ${QUALITY_WORD[w.quality].toLowerCase()}.`,
    ),
    nowInside && nowMs !== undefined ? `Now ${clockAt(nowMs, lon, timezone)}.` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="card" data-mode="vedic">
      <div className="spread" style={{ alignItems: 'flex-start', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div className="h3" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {day.vaara.englishName} · {day.vaara.name}
            <InfoDot term="vaara" />
          </div>
          <div className="tiny muted" style={{ marginTop: 2 }}>
            {dateAt(day.sunriseUtc.getTime(), lon, timezone)}
            {placeLabel ? ` · ${placeLabel}` : ''}
          </div>
        </div>
        <span className="mode-chip" style={{ flexShrink: 0 }}>
          Ruled by{' '}
          <Term id={`graha.${grahaName(day.vaara.lord).toLowerCase()}`}>
            {grahaName(day.vaara.lord)}
          </Term>
        </span>
      </div>

      <div className="tiny faint" style={{ marginTop: 6, lineHeight: 1.5 }}>
        Sunrise {clockAt(day.sunriseUtc.getTime(), lon, timezone)} · Sunset{' '}
        {clockAt(day.sunsetUtc.getTime(), lon, timezone)} · Next sunrise{' '}
        {clockAt(day.nextSunriseUtc.getTime(), lon, timezone)}. Every window below is a fraction of
        that daylight, so they shift with the season and with your city.
      </div>

      {day.estimated && (
        <div className="banner banner--gold tiny" style={{ marginTop: 8 }}>
          The Sun does not properly rise or set here on this date, so 6 am and 6 pm local were used
          to divide the day. Treat these windows as indicative only.
        </div>
      )}

      {/* --- the bar --- */}
      <div
        role="img"
        aria-label={barSummary}
        style={{
          position: 'relative',
          height: 30,
          marginTop: 12,
          borderRadius: 8,
          overflow: 'hidden',
          border: '1px solid var(--border)',
          background: 'var(--card-strong)',
        }}
      >
        {/* Daylight, so the night half of the bar is visibly a different thing. */}
        <div
          aria-hidden
          style={{
            ...blockAt({ startUtc: day.sunriseUtc, endUtc: day.sunsetUtc }),
            background: 'var(--mode-soft)',
          }}
        />
        {named.map((w, i) => (
          <div key={`${w.kind}-${i}`} aria-hidden style={{ ...blockAt(w), ...fillFor(w.quality) }} />
        ))}
        {highlight && (
          <div
            aria-hidden
            style={{
              ...blockAt(highlight),
              border: '2px solid var(--mode)',
              borderRadius: 4,
            }}
          />
        )}
        {nowInside && nowMs !== undefined && (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${pct(nowMs)}%`,
              width: 2,
              background: 'var(--text)',
            }}
          />
        )}
      </div>

      {/* --- legend: the bar carries no text, so this row does --- */}
      <div className="wrap" style={{ gap: 10, marginTop: 8 }}>
        <LegendKey quality="inauspicious">Avoid</LegendKey>
        <LegendKey quality="auspicious">Favourable</LegendKey>
        <span className="row tiny muted" style={{ gap: 6 }}>
          <span
            aria-hidden
            style={{
              width: 16,
              height: 10,
              minWidth: 16,
              borderRadius: 3,
              background: 'var(--mode-soft)',
              border: '1px solid var(--mode-line)',
            }}
          />
          Daylight
        </span>
        {highlight && (
          <span className="row tiny" style={{ gap: 6, color: 'var(--mode-ink)' }}>
            <span
              aria-hidden
              style={{ width: 16, height: 10, minWidth: 16, borderRadius: 3, border: '2px solid var(--mode)' }}
            />
            {highlight.label}
          </span>
        )}
        {nowInside && nowMs !== undefined && (
          <span className="row tiny muted" style={{ gap: 6 }}>
            <span aria-hidden style={{ width: 2, height: 12, background: 'var(--text)' }} />
            Now {clockAt(nowMs, lon, timezone)}
          </span>
        )}
      </div>

      {/* --- named windows, in words --- */}
      <div className="section-title" style={{ marginBottom: 0 }}>
        The named windows
      </div>
      {named.map((w, i) => (
        <NamedRow
          key={`${w.kind}-${i}`}
          window={w}
          lon={lon}
          timezone={timezone}
          term={w.kind === 'rahu-kaal' ? 'rahu-kaal' : undefined}
        />
      ))}
      {!windows.abhijit && (
        <div
          className="tiny muted"
          style={{ padding: '8px 0', borderTop: '1px solid var(--border)', lineHeight: 1.55 }}
        >
          No <Term id="muhurtha">Abhijit Muhurta</Term> today. Wednesday is the one weekday the
          tradition gives none, so the usual all-purpose good window simply is not available — the
          favourable choghadiya below are what is left.
        </div>
      )}

      {/* --- panchanga strip, only when the caller computed it --- */}
      {panchanga && (
        <>
          <div
            className="section-title"
            style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 4 }}
          >
            Panchanga of this day
            <InfoDot term="panchanga" />
          </div>
          <div className="tiny faint" style={{ marginBottom: 8, lineHeight: 1.5 }}>
            Five measures that describe a day in the Indian calendar. Together they are what a
            traditional almanac prints, and what the ranking below is scored against.
          </div>
          <div className="wrap" style={{ gap: 6 }}>
            <span className="chip chip--fact" style={{ display: 'inline-flex', alignItems: 'center' }}>
              Tithi: {panchanga.tithi.name}
              <InfoDot term="tithi" />
            </span>
            <span className="chip chip--fact" style={{ display: 'inline-flex', alignItems: 'center' }}>
              Nakshatra: {panchanga.nakshatra.name}
              <InfoDot term="nakshatra" />
            </span>
            <span className="chip chip--fact" style={{ display: 'inline-flex', alignItems: 'center' }}>
              Yoga: {panchanga.yoga.name}
              <InfoDot term="yoga" />
            </span>
            <span className="chip chip--fact">Karana: {panchanga.karana.name}</span>
          </div>
          <div className="tiny faint" style={{ marginTop: 6, lineHeight: 1.5 }}>
            {panchanga.tithi.paksha === 'shukla'
              ? 'Shukla paksha — the fortnight from new Moon to full, read as the growing half of the month.'
              : 'Krishna paksha — the fortnight from full Moon to new, read as the winding-down half of the month.'}
            {panchanga.karana.isVishti
              ? ' Vishti (Bhadra) karana is running, the half-tithi the tradition says to postpone anything new past.'
              : ''}
          </div>
        </>
      )}

      {/* --- the sixteen choghadiya --- */}
      <div className="spread" style={{ marginTop: 14 }}>
        <div className="section-title" style={{ margin: 0 }}>
          Choghadiya bands
        </div>
        <button
          className="btn btn--ghost btn--small"
          aria-expanded={showBands}
          onClick={() => setShowBands(!showBands)}
        >
          {showBands ? 'Hide' : 'Show all 16'}
        </button>
      </div>

      {showBands && (
        <>
          <div className="tiny faint" style={{ margin: '4px 0 8px', lineHeight: 1.5 }}>
            Choghadiya splits the daylight into eight equal parts and the night into eight more, and
            names each one. It is the everyday layer of muhurta — the one people actually check
            before leaving the house.
          </div>

          <div className="tiny" style={{ color: 'var(--mode-ink)', fontWeight: 600, marginTop: 8 }}>
            Daylight · {clockAt(day.sunriseUtc.getTime(), lon, timezone)} –{' '}
            {clockAt(day.sunsetUtc.getTime(), lon, timezone)}
          </div>
          {windows.dayChoghadiya.map((band, i) => (
            <BandRow key={`d${i}`} band={band} blocker={blockerFor(band)} lon={lon} timezone={timezone} />
          ))}

          <div className="tiny" style={{ color: 'var(--mode-ink)', fontWeight: 600, marginTop: 12 }}>
            Night · {clockAt(day.sunsetUtc.getTime(), lon, timezone)} –{' '}
            {clockAt(day.nextSunriseUtc.getTime(), lon, timezone)}
          </div>
          {windows.nightChoghadiya.map((band, i) => (
            <BandRow key={`n${i}`} band={band} blocker={null} lon={lon} timezone={timezone} />
          ))}
        </>
      )}
    </div>
  );
}
